/**
 * GTM Event Configuration — Single source of truth for all GA4 events.
 * Add new events here; no other file changes needed for config.
 */

export type GtmTriggerType = 'page_load' | 'button_click' | 'server_response';

export interface GtmEventConfig {
    /** GA4 event name pushed to dataLayer */
    eventName: string;
    /** Logical category for grouping */
    category: string;
    /** When this event fires */
    triggerType: GtmTriggerType;
    /** Parameters expected for web platform */
    webParams: string[];
    /** Human-readable description */
    description: string;
}

export const GTM_EVENT_CONFIG: Record<string, GtmEventConfig> = {

    // ───────────────────── Navigation ─────────────────────
    PAGE_VIEW: {
        eventName: 'page_view',
        category: 'Navigation',
        triggerType: 'page_load',
        webParams: ['user_id', 'page_name', 'page_url', 'platform', 'timestamp'],
        description: 'When User navigates to pages',
    },

    // ───────────────────── Onboarding — OTP ─────────────────────
    MOBILE_OTP_VERIFIED_SUCCESS: {
        eventName: 'mobile_otp_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['mobile_number', 'timestamp'],
        description: 'When User successfully verifies mobile OTP',
    },
    MOBILE_OTP_VERIFIED_FAIL: {
        eventName: 'mobile_otp_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['reason', 'attempt_count'],
        description: 'When mobile OTP verification fails',
    },

    // ───────────────────── Onboarding — Employment & Income ─────────────────────
    EMPLOYMENT_SUBMIT: {
        eventName: 'employment_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id'],
        description: 'When User enters employment status and moves to next step',
    },
    INCOME_SUBMIT: {
        eventName: 'income_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'monthly_salary'],
        description: 'When User enters monthly income and moves to next step',
    },
    DOB_SUBMIT_SUCCESS: {
        eventName: 'dob_submit_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'dob'],
        description: 'When user enters DOB',
    },

    // ───────────────────── Onboarding — PAN ─────────────────────
    PAN_VERIFIED_SUCCESS: {
        eventName: 'pan_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'pan_masked', 'verification_time'],
        description: 'When User verifies PAN and moves to next step',
    },
    PAN_VERIFIED_FAIL: {
        eventName: 'pan_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'failure_reason'],
        description: 'When PAN verification fails',
    },

    // ───────────────────── Onboarding — CIBIL ─────────────────────
    CIBIL_FETCHED: {
        eventName: 'cibil_fetched',
        category: 'Onboarding',
        triggerType: 'server_response',
        webParams: ['user_id', 'bureau_source'],
        description: 'When User is prompted with CIBIL score after submitting details',
    },
    NO_CIBIL_FETCHED: {
        eventName: 'no_cibil_fetched',
        category: 'Onboarding',
        triggerType: 'server_response',
        webParams: ['user_id'],
        description: 'When User is NOT prompted with CIBIL score after submitting details',
    },

    // ───────────────────── Onboarding — Company ─────────────────────
    COMPANY_DETAILS_SUBMIT: {
        eventName: 'company_details_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'company_domain'],
        description: 'When User submits company details',
    },
    COMPANY_ADDED_MANUALLY: {
        eventName: 'company_added_manually',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id'],
        description: 'When user manually adds company not in search list',
    },

    // ───────────────────── Onboarding — Email ─────────────────────
    EMAIL_DETAILS_SKIP: {
        eventName: 'email_details_skip',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id'],
        description: 'When user skips email input',
    },
    COMPANY_EMAIL_OTP_VERIFIED_SUCCESS: {
        eventName: 'company_email_otp_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'work_email'],
        description: 'When User successfully verifies Company Email OTP',
    },
    COMPANY_EMAIL_OTP_VERIFIED_FAIL: {
        eventName: 'company_email_otp_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'reason'],
        description: 'When Company Email OTP verification fails',
    },

    // ───────────────────── Onboarding — Company Verification ─────────────────────
    COMPANY_DETAILS_VERIFIED_SUCCESS: {
        eventName: 'company_details_verified_success',
        category: 'Onboarding',
        triggerType: 'server_response',
        webParams: ['user_id', 'verification_mode'],
        description: 'When company details are verified successfully',
    },
    COMPANY_DETAILS_VERIFIED_FAIL: {
        eventName: 'company_details_verified_fail',
        category: 'Onboarding',
        triggerType: 'server_response',
        webParams: ['user_id', 'failure_reason'],
        description: 'When company details verification fails',
    },

    // ───────────────────── Onboarding — Loan Purpose ─────────────────────
    LOAN_PURPOSE_SUBMIT: {
        eventName: 'loan_purpose_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'loan_purpose'],
        description: 'When User submits the purpose for requesting loan',
    },

    // ───────────────────── Onboarding — Aadhaar ─────────────────────
    AADHAR_VERIFIED_SUCCESS: {
        eventName: 'aadhar_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'verification_time'],
        description: 'When User successfully verifies Aadhaar via Digilocker',
    },
    AADHAR_VERIFIED_FAIL: {
        eventName: 'aadhar_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'reason'],
        description: 'When Aadhaar verification fails',
    },

    // ───────────────────── Onboarding — Address ─────────────────────
    ADDRESS_SUBMIT: {
        eventName: 'address_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'application_id', 'approved_amount', 'tenure', 'interest_rate'],
        description: 'When User submits address details',
    },

    // ───────────────────── Onboarding — Selfie ─────────────────────
    SELFIE_VERIFIED_SUCCESS: {
        eventName: 'selfie_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'verification_time'],
        description: 'When User successfully verifies selfie',
    },
    SELFIE_VERIFIED_FAIL: {
        eventName: 'selfie_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'failure_reason'],
        description: 'When selfie verification fails',
    },

    // ───────────────────── Onboarding — Personal Email ─────────────────────
    PERSONAL_EMAIL_OTP_VERIFIED_SUCCESS: {
        eventName: 'personal_email_otp_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id'],
        description: 'When User successfully verifies personal email OTP',
    },
    PERSONAL_EMAIL_OTP_VERIFIED_FAIL: {
        eventName: 'personal_email_otp_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'reason'],
        description: 'When personal email OTP verification fails',
    },

    // ───────────────────── Onboarding — CKYC ─────────────────────
    CKYC_VERIFICATION_SUCCESS: {
        eventName: 'ckyc_verification_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'ckyc_id'],
        description: 'When CKYC verification completes successfully',
    },
    CKYC_VERIFICATION_FAIL: {
        eventName: 'ckyc_verification_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'reason'],
        description: 'When CKYC verification fails',
    },

    // ───────────────────── Onboarding — Bank ─────────────────────
    BANK_DETAILS_SUBMIT: {
        eventName: 'bank_details_submit',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'bank_name', 'account_masked'],
        description: 'When User successfully submits bank account details',
    },
    BANK_VERIFIED_SUCCESS: {
        eventName: 'bank_verified_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'verification_mode'],
        description: 'When bank account is successfully verified',
    },
    BANK_VERIFIED_FAIL: {
        eventName: 'bank_verified_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'failure_reason'],
        description: 'When bank account verification fails',
    },

    // ───────────────────── Onboarding — Video KYC ─────────────────────
    VIDEO_VERIFICATION_SUCCESS: {
        eventName: 'video_verification_success',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'session_id'],
        description: 'When user completes video verification',
    },
    VIDEO_VERIFICATION_FAIL: {
        eventName: 'video_verification_fail',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'reason'],
        description: 'When video verification fails',
    },

    // ───────────────────── Onboarding — Loan Approval & Disbursement ─────────────────────
    LOAN_READY_FOR_DISBURSEMENT: {
        eventName: 'loan_ready_for_disbursement',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'application_id', 'approved_amount', 'tenure', 'interest_rate'],
        description: 'When user is prompted that loan is ready for disbursement',
    },
    LOAN_DECLINED: {
        eventName: 'loan_declined',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'application_id', 'decline_reason'],
        description: 'When user declines loan or loan is denied',
    },
    LOAN_DISBURSED: {
        eventName: 'loan_disbursed',
        category: 'Onboarding',
        triggerType: 'page_load',
        webParams: ['user_id', 'loan_id', 'disbursed_amount', 'disbursal_date'],
        description: 'When loan gets disbursed successfully',
    },

    // ───────────────────── Dashboard ─────────────────────
    LOAN_CLOSED: {
        eventName: 'loan_closed',
        category: 'Dashboard',
        triggerType: 'page_load',
        webParams: ['user_id', 'loan_id', 'closure_date'],
        description: 'When user successfully closes loan',
    },
    EMI_PAID_SUCCESS: {
        eventName: 'emi_paid_success',
        category: 'Dashboard',
        triggerType: 'page_load',
        webParams: ['user_id', 'loan_id', 'emi_number', 'amount_paid', 'payment_mode'],
        description: 'When User pays EMI successfully',
    },
    EMI_PAID_FAIL: {
        eventName: 'emi_paid_fail',
        category: 'Dashboard',
        triggerType: 'page_load',
        webParams: ['user_id', 'loan_id', 'emi_number', 'failure_reason'],
        description: 'When EMI repayment fails',
    },
    REFER_FRIEND: {
        eventName: 'refer_friend',
        category: 'Dashboard',
        triggerType: 'button_click',
        webParams: ['user_id', 'referral_code', 'invite_channel'],
        description: 'When User refers a friend',
    },

    // ───────────────────── User Entry ─────────────────────
    
    REGISTER: {
        eventName: 'register',
        category: 'User Entry',
        triggerType: 'page_load',
        webParams: ['user_id', 'platform', 'register_time'],
        description: 'When user successfully registers',
    },
    LOGIN: {
        eventName: 'login',
        category: 'User Entry',
        triggerType: 'page_load',
        webParams: ['user_id', 'platform', 'login_time'],
        description: 'When user successfully logs in',
    },
    LOGIN_FAIL: {
        eventName: 'login_fail',
        category: 'User Entry',
        triggerType: 'page_load',
        webParams: ['user_id', 'failure_reason'],
        description: 'When user login fails',
    },

    // ───────────────────── Customer Support ─────────────────────
    CONTACT_US_INITIATED: {
        eventName: 'contact_us_initiated',
        category: 'Customer Support',
        triggerType: 'button_click',
        webParams: ['user_id', 'ticket_id', 'issue_category'],
        description: 'When User clicks on Create Query/Call us/Email us',
    },
    CONTACT_US_CHAT_STARTED: {
        eventName: 'contact_us_chat_started',
        category: 'Customer Support',
        triggerType: 'page_load',
        webParams: ['user_id', 'session_id'],
        description: 'When User starts support chat',
    },
};
